/**
 * UCWonderNav — the single shared UCWonder header.
 * Used on the landing page AND inner pages (community, experiences,
 * about, dashboard) so the nav is identical everywhere:
 * logo · UCWonder AI · Log In / My trips · Menu ▾ (Community/Perks/Experiences/Services)
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { LifeBuoy, Users } from 'lucide-react';
import { UCWonderLogo } from './UCWonderLogo';

const NAVY = '#1E3F70';

const LANDING_PATHS = new Set(['/', '/marketplace-preview']);

const DreamBubble = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className={className} aria-hidden="true">
    <g transform="translate(4 0) scale(0.82)">
      <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
    </g>
    <circle cx="8.2" cy="19.4" r="1.4" />
    <circle cx="4.9" cy="22" r="0.7" />
  </svg>
);

export const UCWonderNav = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const isLoggedIn = !!localStorage.getItem('user_token');

  return (
    <div className="sticky top-0 z-40 px-3 sm:px-6 pt-3">
      <div
        className="rounded-2xl border border-white/25 px-4 sm:px-5 py-2.5 flex items-center justify-between gap-4"
        style={{
          background: 'rgba(255,255,255,0.1)',
          backdropFilter: 'blur(14px)',
          WebkitBackdropFilter: 'blur(14px)',
          boxShadow: '0 8px 30px rgba(30,63,112,0.12)',
        }}
      >
        <button
          type="button"
          className="cursor-pointer bg-transparent border-0 p-0"
          onClick={() => (LANDING_PATHS.has(location.pathname)
            ? window.scrollTo({ top: 0, behavior: 'smooth' })
            : navigate('/'))}
          aria-label="UCWonder home"
          data-testid="landing-marketplace-wordmark"
        >
          <UCWonderLogo className="h-6" textClass="text-lg sm:text-xl" />
        </button>
        <div className="flex items-center gap-2">
          {(() => {
            const onWonder = location.pathname.startsWith('/plan') || location.pathname.startsWith('/ucwonder-ai');
            const onProfile = location.pathname.startsWith('/dashboard');
            const onCompanion = location.pathname.startsWith('/companion');
            const pill = (chosen) => chosen
              ? { background: NAVY, color: '#fff' }
              : { background: 'transparent', color: NAVY, border: '1px solid rgba(30,63,112,0.25)' };
            return (
              <>
                <button
                  type="button"
                  onClick={() => navigate('/plan')}
                  className="inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-2.5 sm:px-4 py-2 transition-colors hover:opacity-90"
                  style={pill(onWonder)}
                  data-testid="landing-marketplace-nav-concierge"
                >
                  <DreamBubble className="w-4 h-4" />
                  <span className="hidden sm:inline">Wonder</span>
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/companion')}
                  className="inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-2.5 sm:px-4 py-2 transition-colors hover:opacity-90"
                  style={pill(onCompanion)}
                  data-testid="nav-companion-btn"
                >
                  <LifeBuoy className="w-4 h-4" strokeWidth={1.8} />
                  <span className="hidden sm:inline">In-Trip Assist</span>
                </button>
                {isLoggedIn ? (
                  <button
                    type="button"
                    onClick={() => navigate('/dashboard')}
                    className="inline-flex items-center gap-1.5 text-sm font-medium rounded-full px-3 sm:px-4 py-2 transition-colors hover:opacity-90"
                    style={pill(onProfile)}
                    data-testid="landing-marketplace-my-account"
                  >
                    <Users className="w-4 h-4" strokeWidth={1.8} />
                    <span className="hidden sm:inline">My Profile</span>
                    <span className="sm:hidden">Profile</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => navigate('/login')}
                    className="inline-flex items-center text-sm font-medium rounded-full px-3 sm:px-4 py-2 transition-colors hover:opacity-90"
                    style={pill(false)}
                    data-testid="landing-marketplace-login"
                  >
                    Log In
                  </button>
                )}
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
};
